/**
 * Owen Underwriter v2 — deterministic eligibility rules.
 *
 * These checks run in the browser, never miss, and produce the structured
 * findings that the UI renders directly. The LLM verdict (called separately)
 * uses these findings as ground truth and only contributes a narrative summary.
 *
 * A finding has:
 *   { id, status: 'pass'|'condition'|'fail', title, detail, category, impact }
 *
 * Categories: 'credit', 'capacity', 'collateral', 'capital', 'eligibility', 'documentation'.
 * Impact (0-15) feeds the scoring engine in scoring.js.
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const truthy = (v) => v === true || v === 'true' || v === 'yes' || v === 'Yes';

const fmtMoney = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n) => `${Math.round(n * 10) / 10}%`;

// Best-effort credit score parser (e.g. "740", "700-739", "Excellent")
function parseCreditScore(creditField) {
  if (creditField == null) return null;
  if (typeof creditField === 'number') return creditField;
  const s = String(creditField).trim();
  const directNum = parseInt(s, 10);
  if (Number.isFinite(directNum) && directNum > 300 && directNum < 900) return directNum;
  const range = s.match(/(\d{3})\s*[-–]\s*(\d{3})/);
  if (range) return Math.round((parseInt(range[1], 10) + parseInt(range[2], 10)) / 2);
  // Verbal labels — conservative middle-of-range estimates.
  if (/excellent/i.test(s)) return 760;
  if (/very good/i.test(s)) return 730;
  if (/good/i.test(s)) return 690;
  if (/fair/i.test(s)) return 640;
  if (/poor|bad/i.test(s)) return 580;
  return null;
}

// Sum monthly liability payments from URLA liabilities section.
function totalMonthlyDebts(applicationData) {
  const fromForm = num(applicationData?.productRecommendation?.monthlyDebts);
  if (fromForm) return fromForm;
  const entries = applicationData?.applicationData?.liabilities?.entries || [];
  return entries.reduce((sum, e) => {
    if (truthy(e?.toBePaidOff)) return sum;
    return sum + (num(e?.monthlyPayment) || 0);
  }, 0);
}

// Sum gross monthly income from employmentIncome + otherIncome multi-entries.
function totalMonthlyIncome(applicationData) {
  const empEntries = applicationData?.applicationData?.employmentIncome?.entries || [];
  let monthly = 0;
  empEntries.forEach((e) => {
    monthly += num(e?.baseIncome) || 0;
    monthly += num(e?.overtimeIncome) || 0;
    monthly += num(e?.bonusIncome) || 0;
    monthly += num(e?.commissionIncome) || 0;
    monthly += num(e?.tipsIncome) || 0;
    monthly += num(e?.militaryAllowances) || 0;
  });
  const otherEntries = applicationData?.applicationData?.otherIncome?.entries || [];
  otherEntries.forEach((e) => { monthly += num(e?.monthlyAmount) || 0; });
  if (monthly > 0) return monthly;
  // Fallback to assessment-stated annual income.
  const annual = num(applicationData?.productRecommendation?.annualIncome);
  return annual ? annual / 12 : 0;
}

function computeLoanContext(applicationData) {
  const rec = applicationData?.productRecommendation || {};
  const loanInfo = applicationData?.applicationData?.loanInfo || {};

  const purpose = rec.purchaseOrRefinance || (loanInfo.loanPurpose ? loanInfo.loanPurpose.toLowerCase() : null);
  const isPurchase = purpose === 'purchase' || purpose?.startsWith?.('Purchase');
  const isRefi = purpose === 'refinance' || purpose?.includes?.('efinance');

  const propertyValue = num(loanInfo.purchasePrice) || num(loanInfo.currentPropertyValue) || num(rec.estimatedValue) || 0;
  const downPayment = num(loanInfo.downPaymentAmount) || num(rec.downPayment) || 0;
  const loanAmount =
    num(loanInfo.loanAmount) ||
    (isRefi ? num(loanInfo.currentMortgageBalance) || downPayment : Math.max(0, propertyValue - downPayment));

  const ltv = propertyValue > 0 ? (loanAmount / propertyValue) * 100 : null;
  const downPaymentPct = propertyValue > 0 ? (downPayment / propertyValue) * 100 : null;

  return { purpose, isPurchase, isRefi, propertyValue, downPayment, loanAmount, ltv, downPaymentPct };
}

// ─────────────────────────────────────────────────────────────
// Individual rule runners
// ─────────────────────────────────────────────────────────────
function checkCredit(rec, product) {
  const score = parseCreditScore(rec?.creditScore);
  const min = num(product?.minCreditScore);
  if (!min) {
    return {
      id: 'credit_min', category: 'credit', status: 'condition', impact: 6,
      title: 'Credit Pull Required',
      detail: 'A formal credit pull is needed to confirm score and tradelines.',
    };
  }
  if (score == null) {
    return {
      id: 'credit_min', category: 'credit', status: 'condition', impact: 6,
      title: 'Credit Score Not Yet Stated',
      detail: `This program requires a minimum FICO of ${min}. Owen will need a credit pull to confirm.`,
    };
  }
  if (score >= min + 40) {
    return {
      id: 'credit_min', category: 'credit', status: 'pass', impact: 12,
      title: `Credit Score ${score} comfortably exceeds the ${min} minimum`,
      detail: `Stated credit score is well above the program minimum (${min}). Pricing tier likely favorable.`,
    };
  }
  if (score >= min) {
    return {
      id: 'credit_min', category: 'credit', status: 'pass', impact: 9,
      title: `Credit Score ${score} meets the ${min} minimum`,
      detail: 'Stated credit score is above program minimum. Final pricing depends on credit pull.',
    };
  }
  return {
    id: 'credit_min', category: 'credit', status: 'fail', impact: 0,
    title: `Stated credit score (${score}) is below the ${min} minimum`,
    detail: `This program requires ${min}+. Consider improving credit or selecting an alternative program (FHA / Non-QM).`,
  };
}

function checkLTV(ctx, product) {
  const max = num(product?.maxLTV);
  if (!max) {
    return {
      id: 'ltv', category: 'collateral', status: 'condition', impact: 5,
      title: 'LTV Limits Not Yet Modeled',
      detail: 'Owen could not confirm the program\'s max LTV from the recommendation. Confirm before submission.',
    };
  }
  if (ctx.ltv == null) {
    return {
      id: 'ltv', category: 'collateral', status: 'condition', impact: 4,
      title: 'LTV Not Yet Calculable',
      detail: 'Property value or loan amount missing. Owen needs both to confirm LTV is within program limits.',
    };
  }
  if (ctx.ltv <= max) {
    return {
      id: 'ltv', category: 'collateral', status: 'pass', impact: 10,
      title: `LTV ${fmtPct(ctx.ltv)} is within the ${max}% maximum`,
      detail: `Loan amount ${fmtMoney(ctx.loanAmount)} on a ${fmtMoney(ctx.propertyValue)} property fits the program guideline.`,
    };
  }
  return {
    id: 'ltv', category: 'collateral', status: 'fail', impact: 0,
    title: `LTV ${fmtPct(ctx.ltv)} exceeds the ${max}% program maximum`,
    detail: `Either bring more down payment (${fmtMoney(ctx.loanAmount - (ctx.propertyValue * max) / 100)} additional) or move to a higher-LTV program.`,
  };
}

function checkDownPayment(ctx, product) {
  if (!ctx.isPurchase) return null;
  const minPct = num(product?.minDownPayment);
  if (minPct == null) return null;
  if (ctx.downPaymentPct == null) {
    return {
      id: 'down_payment', category: 'capital', status: 'condition', impact: 4,
      title: 'Down Payment Not Yet Stated',
      detail: `Program requires at least ${minPct}% down. Owen needs purchase price + down payment to confirm.`,
    };
  }
  if (ctx.downPaymentPct >= minPct) {
    return {
      id: 'down_payment', category: 'capital', status: 'pass', impact: 8,
      title: `Down Payment ${fmtPct(ctx.downPaymentPct)} meets the ${minPct}% minimum`,
      detail: `${fmtMoney(ctx.downPayment)} on a ${fmtMoney(ctx.propertyValue)} purchase.`,
    };
  }
  const needed = (ctx.propertyValue * minPct) / 100;
  return {
    id: 'down_payment', category: 'capital', status: 'fail', impact: 0,
    title: `Down Payment ${fmtPct(ctx.downPaymentPct)} is below the ${minPct}% minimum`,
    detail: `An additional ${fmtMoney(needed - ctx.downPayment)} would meet the program minimum.`,
  };
}

function checkDTI(applicationData, ctx, product) {
  const monthlyIncome = totalMonthlyIncome(applicationData);
  const monthlyDebts = totalMonthlyDebts(applicationData);
  // Crude estimated PITI: 0.6% of loan / mo for principal+interest+escrows for now.
  const estimatedPITI = ctx.loanAmount > 0 ? ctx.loanAmount * 0.006 : 0;
  const totalMonthly = monthlyDebts + estimatedPITI;
  const dti = monthlyIncome > 0 ? (totalMonthly / monthlyIncome) * 100 : null;

  if (monthlyIncome <= 0) {
    return {
      id: 'dti', category: 'capacity', status: 'condition', impact: 4,
      title: 'Income Not Yet Stated',
      detail: 'Owen needs gross monthly income to estimate your debt-to-income ratio.',
    };
  }
  if (dti == null) {
    return {
      id: 'dti', category: 'capacity', status: 'condition', impact: 4,
      title: 'DTI Not Yet Calculable',
      detail: 'Owen needs loan amount + monthly debts to estimate DTI.',
    };
  }

  // Default thresholds; programs can override via product.maxDTI in the future.
  const govLoan = product?.productAdherence === 'Government' || /FHA|VA|USDA/i.test(product?.productFamily || '');
  const dtiCap = num(product?.maxDTI) || (govLoan ? 50 : 45);

  if (dti <= dtiCap - 5) {
    return {
      id: 'dti', category: 'capacity', status: 'pass', impact: 11,
      title: `Estimated DTI ${fmtPct(dti)} is comfortably under ${dtiCap}%`,
      detail: `Estimated payment of ${fmtMoney(estimatedPITI)} + ${fmtMoney(monthlyDebts)} in other debts vs. ${fmtMoney(monthlyIncome)} monthly income.`,
    };
  }
  if (dti <= dtiCap) {
    return {
      id: 'dti', category: 'capacity', status: 'condition', impact: 7,
      title: `Estimated DTI ${fmtPct(dti)} is near the ${dtiCap}% program cap`,
      detail: 'Tight but workable. Compensating factors (reserves, large down payment) may be required.',
    };
  }
  return {
    id: 'dti', category: 'capacity', status: 'fail', impact: 0,
    title: `Estimated DTI ${fmtPct(dti)} exceeds the ${dtiCap}% program cap`,
    detail: `Reduce monthly debts by about ${fmtMoney(((dti - dtiCap) / 100) * monthlyIncome)} or increase down payment to reduce loan size.`,
  };
}

function checkOccupancy(rec, applicationData, product) {
  const propertyInfo = applicationData?.applicationData?.propertyInfo;
  const stated = propertyInfo?.occupancy || rec?.propertyUse;
  const isPrimary = /primary/i.test(stated || '') || stated === 'primary';
  const isSecondHome = /second/i.test(stated || '');
  const isInvestment = /investment|rental/i.test(stated || '');

  const allowed =
    (isPrimary && truthy(product?.primaryAllowed)) ||
    (isSecondHome && truthy(product?.secondHomeAllowed)) ||
    (isInvestment && truthy(product?.investmentAllowed));

  if (!stated) {
    return {
      id: 'occupancy', category: 'eligibility', status: 'condition', impact: 4,
      title: 'Occupancy Not Yet Stated',
      detail: 'Confirm whether this is a primary residence, second home, or investment property.',
    };
  }

  if (allowed === undefined || product?.primaryAllowed === undefined) {
    return {
      id: 'occupancy', category: 'eligibility', status: 'pass', impact: 7,
      title: `Occupancy "${stated}" looks supported`,
      detail: 'Program eligibility for this occupancy type was not explicitly set; defaulting to supported.',
    };
  }

  if (allowed) {
    return {
      id: 'occupancy', category: 'eligibility', status: 'pass', impact: 8,
      title: `${stated} occupancy is allowed under this program`,
      detail: `${product.displayName || product.name} explicitly supports this occupancy type.`,
    };
  }

  return {
    id: 'occupancy', category: 'eligibility', status: 'fail', impact: 0,
    title: `${stated} occupancy is NOT allowed under this program`,
    detail: 'Owen will recommend an alternative program when generating the verdict.',
  };
}

function checkSpecialEligibility(rec, applicationData, product) {
  const findings = [];
  if (truthy(product?.veteranEligibilityRequired)) {
    const isMilitary = truthy(rec?.militaryService) ||
      truthy(applicationData?.applicationData?.militaryService?.everServed);
    if (isMilitary) {
      findings.push({
        id: 'va_eligibility', category: 'eligibility', status: 'pass', impact: 8,
        title: 'Military service confirmed',
        detail: 'VA eligibility (DD-214 / COE) will be verified at full underwriting.',
      });
    } else {
      findings.push({
        id: 'va_eligibility', category: 'eligibility', status: 'fail', impact: 0,
        title: 'Program requires veteran eligibility',
        detail: 'Borrower has not indicated military service. This program will not work — Owen should recommend an alternative.',
      });
    }
  }
  if (truthy(product?.tribalEligibilityRequired)) {
    findings.push({
      id: 'tribal_eligibility', category: 'eligibility', status: 'condition', impact: 4,
      title: 'Tribal eligibility documentation required',
      detail: 'NADL programs require enrolled tribal membership and tribal MOU verification.',
    });
  }
  if (truthy(product?.USDAEligibleAreaRequired)) {
    findings.push({
      id: 'usda_area', category: 'eligibility', status: 'condition', impact: 4,
      title: 'USDA-eligible area must be confirmed',
      detail: 'Property address must be in a USDA-designated rural area. Owen will verify with the USDA map.',
    });
  }
  return findings;
}

function checkDocumentation(applicationData) {
  const uploaded = (applicationData?.documents || []).length;
  if (uploaded === 0) {
    return {
      id: 'documentation', category: 'documentation', status: 'condition', impact: 3,
      title: 'No documents uploaded yet',
      detail: 'Owen ran on stated values only. Uploading proof documents (pay stubs, bank statements, etc.) significantly tightens the verdict.',
    };
  }
  if (uploaded < 5) {
    return {
      id: 'documentation', category: 'documentation', status: 'condition', impact: 5,
      title: `${uploaded} documents uploaded — partial verification`,
      detail: 'Owen verified some stated values. Add the remaining required documents for a stronger verdict.',
    };
  }
  return {
    id: 'documentation', category: 'documentation', status: 'pass', impact: 9,
    title: `${uploaded} documents uploaded — solid coverage`,
    detail: 'Owen had real documents to verify against. Verdict is high-confidence.',
  };
}

// ─────────────────────────────────────────────────────────────
// Public: run all rules against the recommended program.
// ─────────────────────────────────────────────────────────────
export function evaluateEligibility(applicationData) {
  const rec = applicationData?.productRecommendation || {};
  const product = applicationData?.recommendedProduct || {};
  const ctx = computeLoanContext(applicationData);

  const findings = [
    checkCredit(rec, product),
    checkLTV(ctx, product),
    checkDownPayment(ctx, product),
    checkDTI(applicationData, ctx, product),
    checkOccupancy(rec, applicationData, product),
    ...checkSpecialEligibility(rec, applicationData, product),
    checkDocumentation(applicationData),
  ].filter(Boolean);

  const summary = {
    passed: findings.filter(f => f.status === 'pass').length,
    conditions: findings.filter(f => f.status === 'condition').length,
    failed: findings.filter(f => f.status === 'fail').length,
    totalChecks: findings.length,
  };

  return { findings, summary, context: ctx };
}

export default { evaluateEligibility };
